import { z } from 'zod';

const ymd = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-09-01');

/** The reports on the Reports page. */
export const REPORT_TYPES = ['sessions', 'daily', 'drivers', 'chargers', 'remittance'] as const;
export type ReportType = (typeof REPORT_TYPES)[number];

export const REPORT_LABELS: Record<ReportType, string> = {
  sessions: 'Sessions',
  daily: 'Daily by driver',
  drivers: 'Monthly by driver',
  chargers: 'Energy by charger',
  remittance: 'Remittance',
};

/**
 * Filters for a report. Dates are whole days in the time zone given, from and to inclusive
 * (Remittance: in each site's own time zone, so a site's month matches its owner's records).
 * With no organisation, everything the person can see.
 */
export const reportQuery = z
  .object({
    from: ymd,
    to: ymd,
    tz: z.string().max(64).default('Australia/Sydney'),
    organisationId: z.string().uuid().optional(),
    driverId: z.string().uuid().optional(),
    /** Leave out sessions that delivered no energy (plugged in, nothing drawn). */
    hideZero: z
      .enum(['true', 'false'])
      .default('true')
      .transform((v) => v === 'true'),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
  })
  .refine((q) => q.from <= q.to, { message: 'The start date is after the end date', path: ['to'] })
  .refine((q) => (Date.parse(q.to) - Date.parse(q.from)) / 86_400_000 <= 3 * 366, { message: 'Choose three years or less', path: ['from'] });
export type ReportQuery = z.input<typeof reportQuery>;

/** OCPP stop reasons, in words. */
export const STOP_REASON_LABELS: Record<string, string> = {
  EVDisconnected: 'The car was unplugged',
  Local: 'Stopped at the charger',
  Remote: 'Stopped from Pulsepoint',
  DeAuthorized: 'The card was refused',
  EmergencyStop: 'Emergency stop',
  HardReset: 'The charger restarted',
  SoftReset: 'The charger restarted',
  PowerLoss: 'The charger lost power',
  Reboot: 'The charger restarted',
  UnlockCommand: 'Unlocked from Pulsepoint',
  Other: 'Other',
};

/** Money worked out from energy and a $/kWh rate, rounded to whole cents. */
export const centsFor = (energyWh: number, ratePerKwh: number) => Math.round((energyWh / 1000) * ratePerKwh * 100);
